import type { CMSSection, CmsPage } from "@/cms/models";
import { ownShadowCss } from "@/utils/form-values";
import {
  BUTTON_STYLES,
  PROPERTY_SORT_KEYS,
  record,
  string,
  type ButtonStyle,
} from "../model";

export const PROPERTY_LIST_TYPE = "PropertyListSection";
export const PROPERTY_LIST_VIEWS = [
  "grid",
  "list",
  "row",
  "duo",
  "map",
] as const;
export type PropertyListView = (typeof PROPERTY_LIST_VIEWS)[number];
export type PropertyListSortKey = (typeof PROPERTY_SORT_KEYS)[number];
/** Core caps `take` at 1000, and `getCases` serves a sold request with `take` 1000 from a cache whose key ignores the
 *  filter, so the list never asks for 1000. */
export const MAX_TAKE = 999;
/** Elasticsearch refuses `skip + take` above 10,000. */
export const MAX_SKIP = 9000;
export const MAX_PAGE_SIZE = 200;
export const MAX_QUERY_LENGTH = 100;
export const NAMESPACE_PATTERN =
  /^dk\.realequity\.(estate|taxonomy)\.[A-Za-z0-9æøåÆØÅ_.-]+$/;
/** Core reads the store as `Guid?`; any other value fails the whole request. */
export const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const SORT_LABELS: Record<PropertyListSortKey, string> = {
  date_desc: "Nyeste først",
  date_asc: "Ældste først",
  price_asc: "Laveste pris",
  price_desc: "Højeste pris",
  postal_code_desc: "Postnummer (høj-lav)",
  postal_code_asc: "Postnummer (lav-høj)",
  housing_area_desc: "Største areal",
  housing_area_asc: "Mindste areal",
};
export interface PropertyListSortOption {
  value: PropertyListSortKey;
  label: string;
  selected: boolean;
}
export const DEFAULT_SORT_OPTIONS: PropertyListSortOption[] =
  PROPERTY_SORT_KEYS.map((value) => ({
    value,
    label: SORT_LABELS[value],
    selected: value === "date_desc",
  }));
export interface PropertyListTypeFilter {
  displayName: string;
  namespace: string;
}
export interface PropertyListButton {
  text: string;
  style: ButtonStyle;
  iconId: string | null;
  iconPosition: "before" | "after";
}
export interface PropertyListSettings {
  /** Liste og kortvisning: the map stands beside the list on computers */
  split: boolean;
  useSearch: boolean;
  showSearch: boolean;
  searchPlaceholder: string;
  searchButtonText: string;
  searchHeading: string;
  clearSearchText: string;
  typeFilterMode: "auto" | "fixed" | "none";
  typeFilters: PropertyListTypeFilter[];
  hiddenTypes: string[];
  typeFilterOrder: "alphabetical" | "count";
  showFilterCounts: boolean;
  allFilterLabel: string;
  openHouseFilter: boolean;
  openHouseLabel: string;
  showSort: boolean;
  sortOptions: PropertyListSortOption[];
  defaultSort: PropertyListSortKey;
  views: PropertyListView[];
  rememberView: boolean;
  gridCard: "gallery" | "editorial";
  listCard: "list" | "editorial";
  editorialCard: { actionLabel: string; actionVisibility: "hover" | "always" };
  /** `none` or `<rest> | <hover>`, read by `getShadow` as the old list's `shadow`. */
  cardShadow: string;
  /** Only on look-system websites, where `cardShadow` may hold own CSS. */
  lookSystem?: true;
  tilt: boolean;
  flyIn: boolean;
  pageSize: number;
  loadMore: boolean;
  loadMoreButton: PropertyListButton;
  showCount: boolean;
  /** the editor's own line; without it `countLine` writes the standard one */
  countText?: string;
  countPosition: "below" | "above";
  pagingGap?: string;
  emptyText: string;
  noResultsText: string;
}
export const PROPERTY_LIST_DEFAULTS = {
  searchPlaceholder: "Adresse, by eller postnr.",
  searchButtonText: "Søg",
  searchHeading: "Søgeresultater for »{q}«",
  clearSearchText: "Ryd søgning",
  allFilterLabel: "Alle",
  openHouseLabel: "Åbent hus",
  actionLabel: "Se bolig",
  loadMoreText: "Hent flere",
  emptyText: "Der er ingen boliger at vise lige nu.",
  noResultsText: "Ingen ejendomme passede til dine søgekriterier.",
  pageSize: 24,
} as const;
const SHADOW_TOKENS = ["none", "sm", "md", "lg", "xl", "2xl"];

/** A label a visitor reads: blank means the standard text. */
function text(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}
function oneOf<T extends string>(values: readonly T[], value: unknown): T {
  return (values as readonly unknown[]).includes(value)
    ? (value as T)
    : values[0];
}
function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}
function shadow(raw: unknown, own: boolean): string {
  if (typeof raw !== "string") return "none";
  const parts = raw.split("|").map((part) => part.trim());
  return parts.length <= 2 &&
    parts.every(
      (part) => SHADOW_TOKENS.includes(part) || (own && ownShadowCss(part)),
    )
    ? parts.join(" | ")
    : "none";
}
function sortOptions(raw: unknown): PropertyListSortOption[] {
  const entries = (Array.isArray(raw) ? raw.flat() : []).map(record);
  const seen = new Set<string>();
  const options = entries.flatMap((entry) => {
    const value = entry.value as PropertyListSortKey;
    if (!(PROPERTY_SORT_KEYS as readonly unknown[]).includes(value)) return [];
    if (seen.has(value)) return [];
    seen.add(value);
    return [
      {
        value,
        label: text(entry.label, SORT_LABELS[value]),
        selected: entry.selected === true,
      },
    ];
  });
  if (!options.length) return DEFAULT_SORT_OPTIONS.map((o) => ({ ...o }));
  const chosen = Math.max(
    0,
    options.findIndex((option) => option.selected),
  );
  return options.map((option, index) => ({
    ...option,
    selected: index === chosen,
  }));
}
function views(raw: unknown, variant: number): PropertyListView[] {
  const chosen = [
    ...new Set(
      (Array.isArray(raw) ? raw : []).filter((value) =>
        (PROPERTY_LIST_VIEWS as readonly unknown[]).includes(value),
      ),
    ),
  ] as PropertyListView[];
  const list = chosen.length
    ? chosen
    : (["grid", "list"] as PropertyListView[]);
  if (variant !== 2) return list;
  const beside = list.filter((view) => view !== "map" && view !== "duo");
  return beside.length ? beside : ["list"];
}

/** Sagsliste's own keys (the shared purpose keys are read by `normalizePurposeSettings`); an absent or unknown value is
 *  the default. */
export function normalizePropertyList(
  raw: unknown,
  variant = 0,
  lookSystem = false,
): PropertyListSettings {
  const value = record(raw);
  const pageSize = Number.isInteger(value.pageSize)
    ? Math.max(1, Math.min(MAX_PAGE_SIZE, Number(value.pageSize)))
    : PROPERTY_LIST_DEFAULTS.pageSize;
  const more = record(value.loadMoreButton);
  const editorial = record(value.editorialCard);
  const sorting = sortOptions(value.sortOptions);
  return {
    split: variant === 2,
    useSearch: flag(value.useSearch, true),
    showSearch: flag(value.showSearch, variant === 1),
    searchPlaceholder: text(
      value.searchPlaceholder,
      PROPERTY_LIST_DEFAULTS.searchPlaceholder,
    ),
    searchButtonText: text(
      value.searchButtonText,
      PROPERTY_LIST_DEFAULTS.searchButtonText,
    ),
    searchHeading: text(
      value.searchHeading,
      PROPERTY_LIST_DEFAULTS.searchHeading,
    ),
    clearSearchText: text(
      value.clearSearchText,
      PROPERTY_LIST_DEFAULTS.clearSearchText,
    ),
    typeFilterMode: oneOf(
      ["auto", "fixed", "none"] as const,
      value.typeFilterMode,
    ),
    typeFilters: (Array.isArray(value.typeFilters) ? value.typeFilters : [])
      .map(record)
      .filter(
        (entry) =>
          NAMESPACE_PATTERN.test(string(entry.namespace)) &&
          string(entry.displayName).trim(),
      )
      .slice(0, 12)
      .map((entry) => ({
        displayName: string(entry.displayName),
        namespace: string(entry.namespace),
      })),
    hiddenTypes: (Array.isArray(value.hiddenTypes) ? value.hiddenTypes : [])
      .filter(
        (entry): entry is string =>
          typeof entry === "string" && NAMESPACE_PATTERN.test(entry),
      )
      .slice(0, 50),
    typeFilterOrder: oneOf(
      ["alphabetical", "count"] as const,
      value.typeFilterOrder,
    ),
    showFilterCounts: flag(value.showFilterCounts, false),
    allFilterLabel: text(
      value.allFilterLabel,
      PROPERTY_LIST_DEFAULTS.allFilterLabel,
    ),
    openHouseFilter: flag(value.openHouseFilter, false),
    openHouseLabel: text(
      value.openHouseLabel,
      PROPERTY_LIST_DEFAULTS.openHouseLabel,
    ),
    showSort: flag(value.showSort, true),
    sortOptions: sorting,
    defaultSort: sorting.find((option) => option.selected)!.value,
    views: views(value.views, variant),
    rememberView: flag(value.rememberView, true),
    gridCard: oneOf(["gallery", "editorial"] as const, value.gridCard),
    listCard: oneOf(["list", "editorial"] as const, value.listCard),
    editorialCard: {
      actionLabel: text(
        editorial.actionLabel,
        PROPERTY_LIST_DEFAULTS.actionLabel,
      ),
      actionVisibility: oneOf(
        ["hover", "always"] as const,
        editorial.actionVisibility,
      ),
    },
    cardShadow: shadow(value.cardShadow, lookSystem),
    ...(lookSystem ? { lookSystem: true as const } : {}),
    tilt: flag(value.tilt, false),
    flyIn: flag(value.flyIn, false),
    pageSize,
    loadMore: flag(value.loadMore, true),
    loadMoreButton: {
      text: text(more.text, PROPERTY_LIST_DEFAULTS.loadMoreText),
      style: oneOf(BUTTON_STYLES, more.style),
      iconId:
        typeof more.iconId === "string" && more.iconId ? more.iconId : null,
      iconPosition: oneOf(["before", "after"] as const, more.iconPosition),
    },
    showCount: flag(value.showCount, true),
    ...(typeof value.countText === "string" && value.countText.trim()
      ? { countText: value.countText }
      : {}),
    countPosition: oneOf(["below", "above"] as const, value.countPosition),
    ...(typeof value.pagingGap === "string"
      ? { pagingGap: value.pagingGap }
      : {}),
    emptyText: text(value.emptyText, PROPERTY_LIST_DEFAULTS.emptyText),
    noResultsText: text(
      value.noResultsText,
      PROPERTY_LIST_DEFAULTS.noResultsText,
    ),
  };
}

/** »Viser {shown} af {total} ejendomme«; the standard line is singular for one case. */
export function countLine(
  list: Pick<PropertyListSettings, "countText">,
  shown: number,
  total: number,
): string {
  const line =
    list.countText ??
    (total === 1
      ? "Viser {shown} af {total} ejendom"
      : "Viser {shown} af {total} ejendomme");
  return line
    .replaceAll("{shown}", String(shown))
    .replaceAll("{total}", String(total));
}

export interface PropertyListParamNames {
  q: string;
  filter: string;
  sort: string;
  openhouse: string;
  take: string;
  view: string;
}
/** The first list on a page reads the old `?filter=` and `?take=` links; every later one numbers its own keys, and the
 *  search word is shared. */
export function listParamNames(ordinal: number): PropertyListParamNames {
  const suffix = ordinal > 0 ? String(ordinal + 1) : "";
  return {
    q: "q",
    filter: `filter${suffix}`,
    sort: `sort${suffix}`,
    openhouse: `openhouse${suffix}`,
    take: `take${suffix}`,
    view: `view${suffix}`,
  };
}
/** The list's place among the page's stored sections of its type (Sagslister by default), the same list the actions
 *  count in; 0 without a page. */
export function listOrdinal(
  page: Pick<CmsPage, "sections"> | undefined,
  section: Pick<CMSSection<unknown>, "id" | "type">,
  type = PROPERTY_LIST_TYPE,
): number {
  const lists = (page?.sections ?? []).filter((entry) => entry?.type === type);
  const same = lists.indexOf(section as CMSSection<unknown>);
  if (same >= 0) return same;
  const byId = section.id
    ? lists.findIndex((entry) => entry.id === section.id)
    : -1;
  return Math.max(0, byId);
}
/** How the actions find the list again: its id, or its place when the stored section has none. */
export function sectionKey(
  section: Pick<CMSSection<unknown>, "id">,
  ordinal: number,
): string {
  return section.id ? String(section.id) : `#${ordinal}`;
}
